/**
 * useFlowchartViewSetup - Setup logic for FlowchartView
 */

import { useMemo, useState, useCallback, useRef, startTransition } from 'react';
import { useFlowchartCore } from './useFlowchartCore';
import useFlowchartRef from './useFlowchartRef';
import useAutoTidyOnFilterChange from './useAutoTidyOnFilterChange';
import { useChainReorder } from './useChainReorder';
import { buildDependencyGraph, findDisconnectedComponents } from '../layout';

export function useFlowchartViewSetup({
  ref,
  tasks,
  statuses,
  nestedSprints,
  allTags,
  customFields = [],
  autoArrange,
  campaignId,
  isMultiCampaignMode,
  trackpadPanSensitivity,
  trackpadZoomSensitivity,
  onDependencyChange,
  onConditionsChange,
  onPositionChange,
  onPositionChangeBatch,
  wrapLastUndoAction,
  onApplyTaskPositions,
  onDeleteTasks,
  onFocusModeChange,
  onNestTasks,
  onPasteTasks,
  onCreateConnectedTask,
  onInsertNodeOnEdge,
  onInsertMilestoneOnEdge,
  onNestCampaign,
  onAddTask,
  initialFocusedTaskIds,
  chains,
  filters,
  showActionableOnly,
  onChainMoveLeft,
  onChainMoveRight,
  // Multi-campaign view props
  onAddCampaignToView,
  multiCampaignCampaigns = [],
  openCampaignPickerForPaste,
  localSprint,
  allSprints = [],
  loadFullSprint,
  activeView = null,
  currentView = null,
}) {
  // Compute chain order from stored chains
  const chainOrder = useMemo(() => {
    if (!chains || chains.length === 0) return null;
    return [...chains]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(c => c.id);
  }, [chains]);

  // ═══════════════════════════════════════════════════════════════
  // TOUCHED CHAINS TRACKING
  // ═══════════════════════════════════════════════════════════════
  // Track which chains have been modified during filter/FOKUS mode.
  // When exiting temporary mode, these chains get their saved positions
  // cleared and re-laid out to avoid absolute position artifacts.

  const touchedChainsRef = useRef(new Set());

  // Build task-to-chain mapping for quick lookup
  const taskToChainMap = useMemo(() => {
    if (!tasks || tasks.length === 0) return new Map();
    const graph = buildDependencyGraph(tasks);
    const chainsData = findDisconnectedComponents(tasks, graph);
    const map = new Map();
    chainsData.forEach(chain => {
      chain.tasks.forEach(task => {
        map.set(task.id, chain.id);
      });
    });
    return map;
  }, [tasks]);

  // Determine if we're in a "temporary" layout mode
  const hasActiveFilters = useMemo(() => {
    const hasFilters = filters && Object.values(filters).some(v => 
      v !== undefined && v !== null && v !== '' && 
      (Array.isArray(v) ? v.length > 0 : true)
    );
    return hasFilters || showActionableOnly;
  }, [filters, showActionableOnly]);

  // Callback for when a chain is touched (node dragged in temporary mode)
  const onChainTouched = useCallback((taskId) => {
    const chainId = taskToChainMap.get(taskId);
    if (chainId) {
      touchedChainsRef.current.add(chainId);
    }
  }, [taskToChainMap]);

  // Core flowchart hook
  const core = useFlowchartCore({
    tasks, statuses, nestedSprints, allTags, customFields, autoArrange, campaignId, isMultiCampaignMode,
    trackpadPanSensitivity, trackpadZoomSensitivity,
    onDependencyChange, onConditionsChange, onPositionChange, onPositionChangeBatch,
    wrapLastUndoAction, onApplyTaskPositions, onDeleteTasks,
    onFocusModeChange, onNestTasks, onPasteTasks, onCreateConnectedTask, onInsertNodeOnEdge, onInsertMilestoneOnEdge, onNestCampaign,
    onAddTask,
    initialFocusedTaskIds,
    chainOrder,
    // Multi-campaign view props
    onAddCampaignToView,
    multiCampaignCampaigns,
    openCampaignPickerForPaste,
    // Filter/FOKUS mode integration
    hasActiveFilters,
    onChainTouched,
    localSprint,
    allSprints,
    loadFullSprint,
    activeView,
    currentView,
  });

  // Imperative ref
  useFlowchartRef(ref, {
    handleAutoLayout: core.handleAutoLayout,
    handleAutoLayoutInstant: core.handleAutoLayoutInstant,
    clearCustomPositionsForNodeIds: core.clearCustomPositionsForNodeIds,
    getNodePositions: () => core.displayPositions,
    getNodeDimensions: () => core.nodeDimensions,
    handleResetView: core.handleResetView,
    handleGoToStart: core.handleGoToStart,
    handleZoomIn: core.handleZoomIn,
    handleZoomOut: core.handleZoomOut,
    zoom: core.zoom,
    snapToGrid: core.snapToGrid,
    setSnapToGrid: core.setSnapToGrid,
    getViewportCenter: core.getViewportCenter,
    findEmptySpaceInViewport: core.findEmptySpaceInViewport,
    findPositionInCampaign: core.findPositionInCampaign,
    setTaskPosition: core.setTaskPosition,
    followTaskAfterLayout: core.followTaskAfterLayout,
    handleFocusTask: core.handleFocusTask,
    handleFocusTasks: core.handleFocusTasks,
    focusSelectedTasks: core.focusSelectedTasks,
    centerOnTask: core.centerOnTask,
    panToPosition: core.panToPosition,
    centerOnSelected: core.centerOnSelected,
    handleExitFocusMode: core.handleExitFocusMode,
    isFocusMode: core.isFocusMode,
    focusedTaskIds: core.focusedTaskIds,
    selectedNodes: core.selectedNodes,
    deleteSelectedNodes: core.deleteSelectedNodes,
    focusTasksInstant: core.focusTasksInstant,
    exitFocusModeInstant: core.exitFocusModeInstant,
  });

  // Stable callback for clearing custom positions (won't change between renders)
  const clearCustomPositions = useCallback(() => {
    core.interaction.setCustomPositions({});
  }, [core.interaction]);

  // Callback to clear saved positions for touched chains
  // This sets task.x/y to undefined so they get fresh auto-layout positions
  const onClearTouchedChainPositions = useCallback((taskIds) => {
    if (!taskIds || taskIds.length === 0) return;
    
    // Use batch position change to clear positions (setting to undefined)
    // This triggers a re-layout for these tasks
    const positionChanges = taskIds.map(taskId => ({
      taskId,
      position: { x: undefined, y: undefined },
      // oldPosition will be captured by the handler
    }));
    
    if (onPositionChangeBatch) {
      // Use skipRecordAction to not clutter undo history
      onPositionChangeBatch(positionChanges, { skipRecordAction: true });
    }
  }, [onPositionChangeBatch]);

  // Auto-tidy on filter change
  const { isAnimatingFilter } = useAutoTidyOnFilterChange({
    filters,
    showActionableOnly,
    isFocusMode: core.isFocusMode,
    handleAutoLayoutInstant: core.handleAutoLayoutInstant,
    handleGoToStart: core.handleGoToStart, // Animated camera movement
    clearCustomPositions, // Clear runtime overrides so tasks fall back to saved positions
    // Touched chains tracking
    touchedChainsRef,
    taskToChainMap,
    tasks,
    onClearTouchedChainPositions,
  });

  // Chain reorder with animation
  const { isAnimatingReorder, handleChainMoveLeftWithSwap, handleChainMoveRightWithSwap } = useChainReorder({
    tasks,
    interaction: core.interaction,
    nodeDimensions: core.nodeDimensions,
    // Prefer on-screen positions so swap matches what braces/cards show
    displayPositions: core.displayPositions || core.autoPositions,
    campaignId,
    onPositionChangeBatch,
    onChainMoveLeft,
    onChainMoveRight,
  });

  // Canvas card highlight when hovering a HUD row.
  // List fill is pure CSS :hover (like the sidebar) — this state must NOT
  // flow back into the HUD or every hover re-renders the lists.
  const [hoveredMissionId, setHoveredMissionId] = useState(null);
  const hoverRafRef = useRef(null);

  const handleMissionHover = useCallback((taskId) => {
    // Let the browser paint :hover first, then sync the canvas.
    if (hoverRafRef.current != null) cancelAnimationFrame(hoverRafRef.current);
    hoverRafRef.current = requestAnimationFrame(() => {
      hoverRafRef.current = null;
      startTransition(() => setHoveredMissionId(taskId));
    });
  }, []);

  const handleMissionLeave = useCallback(() => {
    if (hoverRafRef.current != null) {
      cancelAnimationFrame(hoverRafRef.current);
      hoverRafRef.current = null;
    }
    startTransition(() => setHoveredMissionId(null));
  }, []);

  return {
    core,
    isAnimatingReorder,
    isAnimatingFilter,
    handleChainMoveLeftWithSwap,
    handleChainMoveRightWithSwap,
    hoveredMissionId,
    handleMissionHover,
    handleMissionLeave,
  };
}

export default useFlowchartViewSetup;
